import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Users, BookOpen, Newspaper, MessageSquare, Plus, Edit, Trash2, Eye, TrendingUp, RefreshCw } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import RichTextEditor from '../components/RichTextEditor';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [articles, setArticles] = useState([]);
  const [news, setNews] = useState([]);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingArticle, setEditingArticle] = useState(null);
  const [editingNews, setEditingNews] = useState(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [contentType, setContentType] = useState('article');
  const [editorContent, setEditorContent] = useState('');
  const [refreshingAI, setRefreshingAI] = useState(false);

  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  const articleCategories = ['Beginner Crypto', 'Futures Trading Basics', 'Risk Management', 'Macro Economics Simplified', 'Stock Market Basics', 'On-Chain Analysis'];
  const newsCategories = ['Crypto', 'Stocks', 'Business', 'Macro', 'Regulation', 'ETFs', 'Institutional'];

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, usersRes, articlesRes, newsRes, postsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { withCredentials: true }),
        axios.get(`${API}/admin/users`, { withCredentials: true }),
        axios.get(`${API}/education/articles`),
        axios.get(`${API}/news/posts`),
        axios.get(`${API}/forum/posts`, { withCredentials: true })
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setArticles(articlesRes.data);
      setNews(newsRes.data);
      setPosts(postsRes.data);
    } catch (error) {
      console.error('Error fetching admin data:', error);
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateContent = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData);
    
    // Add rich text content
    data.content = editorContent;
    
    // Convert tags to array
    if (data.tags) {
      data.tags = data.tags.split(',').map(t => t.trim()).filter(Boolean);
    } else {
      data.tags = [];
    }

    try {
      if (contentType === 'article') {
        await axios.post(`${API}/education/articles`, data, { withCredentials: true });
        toast.success('Article created');
      } else {
        await axios.post(`${API}/news/posts`, data, { withCredentials: true });
        toast.success('News post created');
      }
      setCreateDialogOpen(false);
      setEditorContent('');
      fetchData();
      e.target.reset();
    } catch (error) {
      console.error('Error creating content:', error);
      toast.error('Failed to create content');
    }
  };

  const handleRefreshAISummary = async () => {
    setRefreshingAI(true);
    try {
      await axios.post(`${API}/ai/market-summary/refresh`, {}, { withCredentials: true });
      toast.success('AI Market Summary refreshed');
    } catch (error) {
      console.error('Error refreshing AI summary:', error);
      toast.error('Failed to refresh AI summary');
    } finally {
      setRefreshingAI(false);
    }
  };

  const handleDeleteArticle = async (articleId) => {
    if (!window.confirm('Delete this article?')) return;
    try {
      await axios.delete(`${API}/education/articles/${articleId}`, { withCredentials: true });
      toast.success('Article deleted');
      fetchData();
    } catch (error) {
      console.error('Error deleting article:', error);
      toast.error('Failed to delete article');
    }
  };

  const handleDeleteNews = async (newsId) => {
    if (!window.confirm('Delete this news post?')) return;
    try {
      await axios.delete(`${API}/news/posts/${newsId}`, { withCredentials: true });
      toast.success('News post deleted');
      fetchData();
    } catch (error) {
      console.error('Error deleting news:', error);
      toast.error('Failed to delete news');
    }
  };

  const handlePinPost = async (postId) => {
    try {
      await axios.put(`${API}/forum/posts/${postId}/pin`, {}, { withCredentials: true });
      toast.success('Post pin status updated');
      fetchData();
    } catch (error) {
      console.error('Error pinning post:', error);
      toast.error('Failed to update pin status');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#C5A059]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Navbar />

      <div className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {/* Header */}
          <div className="flex justify-between items-center mb-12">
            <div>
              <h1 className="font-manrope font-bold text-4xl sm:text-5xl text-white mb-4" data-testid="admin-title">
                Admin <span className="text-[#C5A059]">Dashboard</span>
              </h1>
              <p className="text-gray-300 text-lg">Manage content and users</p>
            </div>

            <div className="flex items-center gap-3">
              <Button
                onClick={handleRefreshAISummary}
                disabled={refreshingAI}
                variant="outline"
                className="border-[#007AFF]/50 text-[#007AFF] hover:bg-[#007AFF]/10"
                data-testid="refresh-ai-button"
              >
                <RefreshCw size={16} className={`mr-2 ${refreshingAI ? 'animate-spin' : ''}`} />
                Refresh AI Summary
              </Button>
              
              <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
                <DialogTrigger asChild>
                  <Button
                    className="bg-[#C5A059] text-black hover:bg-[#D4AF37] rounded-sm px-6 py-3 font-semibold shadow-[0_0_15px_rgba(197,160,89,0.3)] hover:shadow-[0_0_25px_rgba(197,160,89,0.5)] transition-all"
                    data-testid="create-content-button"
                  >
                    <Plus size={20} className="mr-2" />
                    Create Content
                  </Button>
                </DialogTrigger>
              <DialogContent className="bg-[#111111] border-white/10 text-white max-w-2xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-2xl font-bold text-white">Create New Content</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateContent} className="space-y-4 mt-4">
                  <Select value={contentType} onValueChange={setContentType}>
                    <SelectTrigger className="bg-black/50 border-white/10 text-white rounded-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#111111] border-white/10 text-white">
                      <SelectItem value="article">Education Article</SelectItem>
                      <SelectItem value="news">News Post</SelectItem>
                    </SelectContent>
                  </Select>

                  <Select name="category" required>
                    <SelectTrigger className="bg-black/50 border-white/10 text-white rounded-sm">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#111111] border-white/10 text-white">
                      {(contentType === 'article' ? articleCategories : newsCategories).map((cat) => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Input name="title" placeholder="Title" required className="bg-black/50 border-white/10 text-white" />
                  
                  {contentType === 'news' && (
                    <Input name="summary" placeholder="Summary" required className="bg-black/50 border-white/10 text-white" />
                  )}
                  
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Content</label>
                    <RichTextEditor
                      value={editorContent}
                      onChange={setEditorContent}
                      placeholder="Write your content here..."
                    />
                  </div>
                  <Input name="image" placeholder="Image URL (optional)" className="bg-black/50 border-white/10 text-white" />
                  <Input name="tags" placeholder="Tags (comma separated)" className="bg-black/50 border-white/10 text-white" />

                  {contentType === 'news' && (
                    <label className="flex items-center gap-2 text-white">
                      <input type="checkbox" name="featured" className="rounded" />
                      <span>Feature this post</span>
                    </label>
                  )}

                  <Button type="submit" className="bg-[#C5A059] text-black hover:bg-[#D4AF37] w-full">
                    Create {contentType === 'article' ? 'Article' : 'News Post'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-12">
            <div className="bg-[#111111] border border-white/10 rounded-lg p-6">
              <div className="flex items-center gap-3 mb-2">
                <Users className="text-[#C5A059]" size={24} />
                <h3 className="text-gray-400 text-sm">Total Users</h3>
              </div>
              <p className="text-3xl font-bold text-white">{stats?.users || 0}</p>
            </div>
            <div className="bg-[#111111] border border-white/10 rounded-lg p-6">
              <div className="flex items-center gap-3 mb-2">
                <BookOpen className="text-[#C5A059]" size={24} />
                <h3 className="text-gray-400 text-sm">Articles</h3>
              </div>
              <p className="text-3xl font-bold text-white">{stats?.articles || 0}</p>
            </div>
            <div className="bg-[#111111] border border-white/10 rounded-lg p-6">
              <div className="flex items-center gap-3 mb-2">
                <Newspaper className="text-[#007AFF]" size={24} />
                <h3 className="text-gray-400 text-sm">News Posts</h3>
              </div>
              <p className="text-3xl font-bold text-white">{stats?.news || 0}</p>
            </div>
            <div className="bg-[#111111] border border-white/10 rounded-lg p-6">
              <div className="flex items-center gap-3 mb-2">
                <MessageSquare className="text-[#C5A059]" size={24} />
                <h3 className="text-gray-400 text-sm">Forum Posts</h3>
              </div>
              <p className="text-3xl font-bold text-white">{stats?.forum_posts || 0}</p>
            </div>
            <div className="bg-[#111111] border border-white/10 rounded-lg p-6">
              <div className="flex items-center gap-3 mb-2">
                <MessageSquare className="text-[#007AFF]" size={24} />
                <h3 className="text-gray-400 text-sm">Comments</h3>
              </div>
              <p className="text-3xl font-bold text-white">{stats?.comments || 0}</p>
            </div>
          </div>

          {/* Content Tabs */}
          <Tabs defaultValue="articles" className="space-y-6">
            <TabsList className="bg-[#111111] border border-white/10 p-1">
              <TabsTrigger value="articles" className="data-[state=active]:bg-[#C5A059] data-[state=active]:text-black">
                Education Articles
              </TabsTrigger>
              <TabsTrigger value="news" className="data-[state=active]:bg-[#007AFF] data-[state=active]:text-white">
                News Posts
              </TabsTrigger>
              <TabsTrigger value="forum" className="data-[state=active]:bg-[#C5A059] data-[state=active]:text-black">
                Forum Posts
              </TabsTrigger>
              <TabsTrigger value="users" className="data-[state=active]:bg-[#007AFF] data-[state=active]:text-white">
                Users
              </TabsTrigger>
            </TabsList>

            {/* Articles Tab */}
            <TabsContent value="articles">
              <div className="space-y-4">
                {articles.map((article) => (
                  <div key={article.article_id} className="bg-[#111111] border border-white/10 rounded-lg p-6 flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="bg-[#C5A059]/20 text-[#C5A059] text-xs px-2 py-1 rounded-full">
                          {article.category}
                        </span>
                      </div>
                      <h3 className="font-manrope font-semibold text-white text-lg mb-1">{article.title}</h3>
                      <p className="text-gray-400 text-sm mb-2">By {article.author_name}</p>
                      <p className="text-gray-500 text-xs">
                        {new Date(article.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Link to={`/education/${article.article_id}`}>
                        <Button variant="ghost" size="sm" className="text-[#007AFF] hover:text-[#2E90FA] hover:bg-white/5">
                          <Eye size={16} />
                        </Button>
                      </Link>
                      <Button
                        onClick={() => handleDeleteArticle(article.article_id)}
                        variant="ghost"
                        size="sm"
                        className="text-red-400 hover:text-red-300 hover:bg-white/5"
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>

            {/* News Tab */}
            <TabsContent value="news">
              <div className="space-y-4">
                {news.map((item) => (
                  <div key={item.news_id} className="bg-[#111111] border border-white/10 rounded-lg p-6 flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="bg-[#007AFF]/20 text-[#007AFF] text-xs px-2 py-1 rounded-full">
                          {item.category}
                        </span>
                        {item.featured && (
                          <span className="bg-[#C5A059]/20 text-[#C5A059] text-xs px-2 py-1 rounded-full flex items-center gap-1">
                            <TrendingUp size={12} />
                            Featured
                          </span>
                        )}
                      </div>
                      <h3 className="font-manrope font-semibold text-white text-lg mb-1">{item.title}</h3>
                      <p className="text-gray-400 text-sm mb-2">By {item.author_name}</p>
                      <p className="text-gray-500 text-xs">
                        {new Date(item.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Link to={`/news/${item.news_id}`}>
                        <Button variant="ghost" size="sm" className="text-[#007AFF] hover:text-[#2E90FA] hover:bg-white/5">
                          <Eye size={16} />
                        </Button>
                      </Link>
                      <Button
                        onClick={() => handleDeleteNews(item.news_id)}
                        variant="ghost"
                        size="sm"
                        className="text-red-400 hover:text-red-300 hover:bg-white/5"
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>

            {/* Forum Tab */}
            <TabsContent value="forum">
              <div className="space-y-4">
                {posts.map((post) => (
                  <div key={post.post_id} className="bg-[#111111] border border-white/10 rounded-lg p-6 flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="bg-[#007AFF]/20 text-[#007AFF] text-xs px-2 py-1 rounded-full">
                          {post.category}
                        </span>
                        {post.pinned && (
                          <span className="bg-[#C5A059]/20 text-[#C5A059] text-xs px-2 py-1 rounded-full">
                            Pinned
                          </span>
                        )}
                      </div>
                      <h3 className="font-manrope font-semibold text-white text-lg mb-1">{post.title}</h3>
                      <p className="text-gray-400 text-sm mb-2">
                        By {post.author_name} • {post.votes} votes • {post.comment_count} comments
                      </p>
                      <p className="text-gray-500 text-xs">
                        {new Date(post.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Link to={`/forum/${post.post_id}`}>
                        <Button variant="ghost" size="sm" className="text-[#007AFF] hover:text-[#2E90FA] hover:bg-white/5">
                          <Eye size={16} />
                        </Button>
                      </Link>
                      <Button
                        onClick={() => handlePinPost(post.post_id)}
                        variant="ghost"
                        size="sm"
                        className={post.pinned ? 'text-[#C5A059] hover:bg-white/5' : 'text-gray-400 hover:text-[#C5A059] hover:bg-white/5'}
                      >
                        {post.pinned ? 'Unpin' : 'Pin'}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>

            {/* Users Tab */}
            <TabsContent value="users">
              <div className="space-y-4">
                {users.map((user) => (
                  <div key={user.user_id} className="bg-[#111111] border border-white/10 rounded-lg p-6 flex justify-between items-center">
                    <div>
                      <h3 className="font-manrope font-semibold text-white mb-1">{user.name}</h3>
                      <p className="text-gray-400 text-sm mb-1">{user.email}</p>
                      <div className="flex items-center gap-2">
                        <span className={`text-xs px-2 py-1 rounded-full ${user.role === 'admin' ? 'bg-[#C5A059]/20 text-[#C5A059]' : 'bg-white/10 text-white'}`}>
                          {user.role}
                        </span>
                        <span className="text-gray-500 text-xs">
                          Reputation: {user.reputation}
                        </span>
                      </div>
                    </div>
                    <p className="text-gray-500 text-xs">
                      Joined {new Date(user.created_at).toLocaleDateString()}
                    </p>
                  </div>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default AdminDashboard;
